"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  Search,
} from "lucide-react";
import { BreedGuide, getBreedGuides } from "@/features/marketplace/api";
import { useCatalogDogs } from "@/features/marketplace/useCatalogDogs";
import { useAppStore } from "@/store/useAppStore";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { DogCard } from "@/components/DogCard";

const akcTopBreedSlugs = [
  "french-bulldog", "labrador-retriever", "golden-retriever", "german-shepherd-dog",
  "dachshund", "poodle", "beagle", "rottweiler", "german-shorthaired-pointer",
  "bulldog", "cane-corso", "cavalier-king-charles-spaniel",
] as const;

export default function Home() {
  const dogs = useCatalogDogs();
  const [breedGuides,setBreedGuides]=useState<BreedGuide[]>([]);
  useEffect(()=>{getBreedGuides().then((items)=>setBreedGuides(akcTopBreedSlugs.flatMap((slug)=>{const item=items.find((breed)=>breed.slug===slug);return item?[item]:[]}))).catch(()=>setBreedGuides([]));},[]);
  const router = useRouter();
  const { query, setQuery } = useAppStore();
  const filtered = dogs.filter((dog) =>
    `${dog.name}${dog.breed}${dog.location}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );
  const startSearch = () =>
    router.push(`/dogs${query ? `?q=${encodeURIComponent(query)}` : ""}`);

  return (
    <main>
      <SiteHeader />

      <section className="hero shell">
        <div className="hero-copy">
          <h1>
            평생을 함께할
            <br />
            가족을 만나보세요.
          </h1>
          <p>
            건강 기록부터 자란 환경까지 꼼꼼하게 살펴보고,
            <br />
            책임감 있는 브리더와 천천히 대화를 시작하세요.
          </p>
          <div className="search-box">
            <label htmlFor="home-dog-search">
              <Search size={20} />
              <span>어떤 반려견을 찾으세요?</span>
            </label>
            <input
              id="home-dog-search"
              aria-label="강아지 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && startSearch()}
              placeholder="견종, 이름 또는 지역을 검색해보세요"
            />
            <button onClick={startSearch}>찾아보기</button>
          </div>
          <div className="popular">
            <span>인기 검색</span>
            {["골든 리트리버", "비숑", "소형견", "서울 근교"].map((item) => (
              <button
                key={item}
                onClick={() => {
                  setQuery(item);
                  router.push(`/dogs?q=${encodeURIComponent(item)}`);
                }}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <div className="hero-art">
          <div className="hero-photo">
            <Image
              src="/images/ai/hero-dog.png"
              alt="초원에 앉아 있는 건강한 강아지"
              fill
              priority
              sizes="(max-width: 800px) 100vw, 48vw"
            />
          </div>
        </div>
      </section>

      <section className="section shell" id="dogs">
        <div className="section-head">
          <div>
            <span className="eyebrow">MEET YOUR FAMILY</span>
            <h2>새로운 가족을 기다리고 있어요</h2>
            <p>성격과 생활 환경을 살펴보며 나와 잘 맞는 친구를 찾아보세요.</p>
          </div>
          <Link className="link-btn" href="/dogs">
            모든 강아지 보기 <ArrowRight size={18} />
          </Link>
        </div>
        <div className="filters" aria-label="빠른 탐색">
          <Link href="/dogs">전체 강아지 <ArrowRight size={16} /></Link>
          <Link href="/breeds">견종 가이드 <ArrowRight size={16} /></Link>
          <Link href="/dogs">지역별 찾기 <ArrowRight size={16} /></Link>
          <Link href="/#promise">책임 분양 기준 <ArrowRight size={16} /></Link>
        </div>
        <div className="dog-grid">
          {filtered.slice(0, 3).map((dog) => (
            <DogCard dog={dog} key={dog.id} />
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="empty">
            검색 결과가 없어요. 다른 견종이나 지역으로 찾아보세요.
          </div>
        )}
      </section>

      <section className="promise" id="promise">
        <div className="shell promise-inner">
          <div className="promise-copy">
            <span className="eyebrow light">THE ONDOG STANDARD</span>
            <h2>
              좋은 브리더를
              <br />
              알아보는 기준이 있어요
            </h2>
            <p>
              온독은 단순히 강아지를 소개하지 않습니다. 공개된 자료와 돌봄
              방식을 기준에 따라 확인하고, 구매자가 직접 판단할 수 있도록 근거를
              보여드립니다.
            </p>
            <Link href="/dogs">
              기준을 충족한 친구 만나기 <ArrowRight size={18} />
            </Link>
          </div>
          <div className="standards">
            {[
              ["01", "건강", "부모견의 품종별 권고 검사와 건강 기록"],
              ["02", "환경", "안전하고 청결한 생활 공간과 충분한 활동"],
              ["03", "사회화", "생애 초기 경험과 기질에 맞춘 세심한 돌봄"],
              ["04", "책임", "투명한 계약과 반환·재분양 지원 정책"],
            ].map(([n, t, d]) => (
              <div className="standard" key={n}>
                <span>{n}</span>
                <div>
                  <h3>{t}</h3>
                  <p>{d}</p>
                </div>
                <ArrowRight />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section shell" id="breeds">
        <div className="section-head">
          <div>
            <span className="eyebrow">FIND YOUR MATCH</span>
            <h2>어떤 친구와 잘 맞을까요?</h2>
            <p>외모보다 활동량, 성격, 돌봄 시간을 먼저 생각해 보세요.</p>
          </div>
          <Link className="link-btn" href="/breeds">
            모든 견종 가이드 <ArrowRight size={18} />
          </Link>
        </div>
        <div className="breed-row">
          {breedGuides.map((breed, i) => (
            <Link key={breed.slug} href={`/breeds/${breed.slug}`}>
              <span>{String(i + 1).padStart(2, "0")}</span>
              <b>{breed.name}</b>
              <small>2025 AKC 인기 순위</small>
            </Link>
          ))}
        </div>
        <div className="home-breed-match"><p>인기보다 중요한 건 나의 생활과 잘 맞는지예요.</p><Link href="/breed-match">내 성향에 맞는 짝꿍 찾기 <ArrowRight size={17} /></Link></div>
      </section>

      <section className="story" id="story">
        <div className="shell story-inner">
          <span>함께 맞이하는 평생</span>
          <h2>
            좋은 만남은 더 많이 묻고,
            <br />더 오래 살펴보는 데서 시작됩니다.
          </h2>
          <Link className="story-cta" href="/dogs">
            나의 가족 찾기 <ArrowRight size={18} />
          </Link>
        </div>
      </section>
      <SiteFooter />
    </main>
  );
}
